/**
 * Content types the browser will not work out on its own.
 *
 * `File.type` is derived from the OS/browser's extension mapping, and Chrome
 * has no entry for HEIC — an iPhone photo arrives as `type: ""`. Every other
 * accepted format resolves fine, which is why this list is short rather than a
 * full MIME table.
 */
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  heic: "image/heic",
  heif: "image/heif",
};

const UNKNOWN_CONTENT_TYPE = "application/octet-stream";

/**
 * The content type to store the file under.
 *
 * **Never empty.** `uploadToSignedUrl` sends whatever it is given as the
 * `Content-Type` header, and an empty one fails the request outright — which is
 * how HEIC uploads broke when credential uploads moved off TUS: the resumable
 * endpoint tolerated a blank type, a direct PUT does not. The bytes never
 * reached storage, so the file looked uploaded and then quietly had nothing to
 * scan.
 */
export function contentTypeFor(file: { type?: string; name?: string }): string {
  if (file.type) {
    return file.type;
  }
  const extension = file.name?.split(".").pop()?.toLowerCase() ?? "";
  return CONTENT_TYPE_BY_EXTENSION[extension] ?? UNKNOWN_CONTENT_TYPE;
}

/**
 * Re-wrap a blob so its OWN type is the one we intend to store.
 *
 * Passing `{ contentType }` to storage-js is not enough: it sends a `Blob`
 * body as a multipart part whose type comes from the blob itself, so a HEIC —
 * which the browser types as `""` — arrives as `application/octet-stream`.
 * Buckets with `allowed_mime_types` (credentials, expense-receipts, …) list
 * `image/heic` but not `application/octet-stream`, so storage rejects it with
 * a 415 that names a type nobody chose.
 */
export function withContentType(blob: Blob, contentType: string): Blob {
  if (blob.type === contentType) {
    return blob;
  }
  return blob.slice(0, blob.size, contentType);
}
